import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, IconButton, Drawer, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggleButton from './ThemeToggleButton';
import { navLinks } from './content';

// Extra breathing room below the sticky navbar so a section's heading isn't
// flush against it once scrolled into view.
const SCROLL_OFFSET_GAP = 16;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const ids = navLinks.map((link) => link.href.split('#')[1]).filter(Boolean);
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Keep a CSS var in sync with the navbar's real rendered height so section
  // scroll offsets (see SectionContainer) stay accurate across breakpoints.
  useEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--navbar-height',
        `${node.getBoundingClientRect().height + SCROLL_OFFSET_GAP}px`
      );
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Land on the right section (with correct offset) when arriving on the
  // landing page with a hash already in the URL, e.g. navigating here from
  // another page's nav link, or opening a #section link directly.
  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => scrollToId(id));
  }, [location.pathname, location.hash]);

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.replaceState(null, '', '/');
    } else {
      navigate('/');
    }
  };

  const handleNavLinkClick = (event, href) => {
    const [path, id] = href.split('#');
    if (!id) return;

    event.preventDefault();
    if ((path === '' || path === '/') && location.pathname === '/') {
      scrollToId(id);
      window.history.replaceState(null, '', `#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <Box ref={navRef} sx={{ position: 'sticky', top: 0, zIndex: 1100, pt: { xs: 1.5, md: 2.5 }, px: { xs: 1.5, sm: 3, md: 6 } }}>
      <Box
        sx={(theme) => ({
          maxWidth: 1240,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 999,
          px: { xs: 1.25, sm: 2, md: 2.5 },
          py: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(20,22,29,0.78)' : 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 24px rgba(0,0,0,0.45)' : '0 4px 24px rgba(15,23,42,0.08)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            bgcolor: '#fff',
            borderRadius: 999,
            px: 1.25,
            py: 0.75,
          }}
          onClick={handleLogoClick}
        >
          <img src="/logo.svg" alt="Remembr" style={{ height: 26, display: 'block' }} />
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 4 }}>
          {navLinks.map((link) => {
            const isActive = activeId && link.href.endsWith(`#${activeId}`);
            return (
              <Typography
                key={link.href}
                component="a"
                href={link.href}
                onClick={(event) => handleNavLinkClick(event, link.href)}
                sx={{
                  textDecoration: 'none',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 14.5,
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {link.label}
              </Typography>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 } }}>
          <ThemeToggleButton />
          <Button
            onClick={() => navigate('/login')}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              textTransform: 'none',
              borderRadius: 999,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Sign in
          </Button>
          <Button
            onClick={() => navigate('/welcome')}
            variant="contained"
            sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 600, px: 2.5, boxShadow: 'none' }}
          >
            Get Started
          </Button>
          <IconButton
            aria-label="Open menu"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, p: 2 }} role="presentation">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton aria-label="Close menu" onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {navLinks.map((link) => {
              const isActive = activeId && link.href.endsWith(`#${activeId}`);
              return (
                <ListItemButton
                  key={link.href}
                  component="a"
                  href={link.href}
                  selected={isActive}
                  onClick={(event) => {
                    setMobileOpen(false);
                    handleNavLinkClick(event, link.href);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    color: isActive ? 'primary.main' : 'text.primary',
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{ fontWeight: isActive ? 700 : 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            <ListItemButton onClick={() => { setMobileOpen(false); navigate('/login'); }}>
              <ListItemText primary="Sign in" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Navbar;
